import React, { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

type MedicalCondition = {
  status: boolean;
  note: string;
};

export interface PatientData {
  registerdDate: string;
  regNo: string;
  fullName: string;
  gender: string;
  maritalStatus: string;
  dob: string;
  religion: string;
  whatsapp: string;
  smobileNo: string;
  mobileNo: string;
  occupation: string;
  bloodGroup: string;
  image: string;
  nic: string;
  title: string;
  email: string;
  address: string;
  nameOfIntroduce: string;
  patientType: string;
  allergies: MedicalCondition;
  gastricProblem: MedicalCondition;
  highBP: MedicalCondition;
  diabetes: MedicalCondition;
  monthlyInjection: MedicalCondition;
  pregnancyBreastFeeding: MedicalCondition;
  coughColdFever: MedicalCondition;
  chestpainHeartProblem: MedicalCondition;
  epilepsy: MedicalCondition;
  previousExtraction: MedicalCondition;
  previoussurgeries: MedicalCondition;
  covid193Doses: MedicalCondition;
  drugHistory: MedicalCondition;
  extraOralExamination: string;
  intraOralExamination: string;
}

interface PatientSummaryModalProps {
  patient?: PatientData;
  onClose?: () => void;
}

const DetailItem: React.FC<{ label: string; value?: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-3">
    <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">
      {label}
    </p>
    <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{value || "N/A"}</p>
  </div>
);

const ConditionItem: React.FC<{ label: string; data?: MedicalCondition }> = ({
  label,
  data,
}) => (
  <div
    className={`rounded-lg p-3 ${data?.status ? "bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800/50" : "bg-slate-50 dark:bg-slate-700/50"}`}
  >
    <div className="flex items-center justify-between">
      <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{label}</p>
      {data?.status ? (
        <CheckCircleIcon className="text-green-500" sx={{ fontSize: 18 }} />
      ) : (
        <CancelIcon className="text-red-500" sx={{ fontSize: 18 }} />
      )}
    </div>

    {data?.status && data?.note && (
      <p className="text-xs text-green-600 dark:text-green-400 mt-1">Note: {data?.note}</p>
    )}
  </div>
);

const formatDate = (dateString?: string) => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString();
};

const PatientModal: React.FC<PatientSummaryModalProps> = ({
  patient,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<"general" | "medical">("general");

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between bg-gradient-to-r from-primary to-primaryLight">
          <h2 className="text-xl font-bold text-white">Patient Summary</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Profile Header */}
          <div className="flex items-center gap-4 mb-6">
            {patient?.image ? (
              <img
                src={patient.image}
                alt={patient.fullName}
                className="w-16 h-16 rounded-xl object-cover border border-slate-200 dark:border-slate-600"
              />
            ) : (
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary to-primaryLight flex items-center justify-center border border-slate-200 dark:border-slate-600">
                <PersonIcon className="text-white" sx={{ fontSize: 32 }} />
              </div>
            )}
            <div>
              <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">{`${patient?.title || ""} ${patient?.fullName || "N/A"}`}</h3>
              <span className="px-2 py-1 bg-indigo-100 dark:bg-primary/20 text-indigo-700 dark:text-slate-200 rounded-full text-xs font-medium">
                {patient?.patientType || "Normal"} Patient
              </span>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 p-1 bg-slate-100 dark:bg-slate-700/50 rounded-lg">
            <button
              onClick={() => setActiveTab("general")}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                activeTab === "general"
                  ? "bg-white dark:bg-slate-600 text-indigo-600 dark:text-slate-100 shadow-sm"
                  : "text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
              }`}
            >
              General Details
            </button>
            <button
              onClick={() => setActiveTab("medical")}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                activeTab === "medical"
                  ? "bg-white dark:bg-slate-600 text-indigo-600 dark:text-slate-100 shadow-sm"
                  : "text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
              }`}
            >
              Medical History
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === "general" && (
            <div className="space-y-6">
              <div>
                <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
                  Personal Information
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <DetailItem
                    label="Registered Date"
                    value={formatDate(patient?.registerdDate)}
                  />
                  <DetailItem label="Reg Number" value={patient?.regNo} />
                  <DetailItem label="Gender" value={patient?.gender} />
                  <DetailItem
                    label="Date of Birth"
                    value={formatDate(patient?.dob)}
                  />
                  <DetailItem label="NIC" value={patient?.nic} />
                  <DetailItem
                    label="Marital Status"
                    value={patient?.maritalStatus}
                  />
                  <DetailItem label="Religion" value={patient?.religion} />
                  <DetailItem label="Occupation" value={patient?.occupation} />
                  <DetailItem label="Blood Group" value={patient?.bloodGroup} />
                  <DetailItem
                    label="Introduction"
                    value={patient?.nameOfIntroduce}
                  />
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
                  Contact Information
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <DetailItem label="Mobile No" value={patient?.mobileNo} />
                  <DetailItem label="WhatsApp" value={patient?.whatsapp} />
                  <DetailItem label="Secondary No" value={patient?.smobileNo} />
                  <DetailItem label="Email" value={patient?.email} />
                  <div className="col-span-2">
                    <DetailItem label="Address" value={patient?.address} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "medical" && (
            <div className="space-y-6">
              <div>
                <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
                  Medical Conditions
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <ConditionItem label="Allergies" data={patient?.allergies} />
                  <ConditionItem
                    label="Gastric Problem"
                    data={patient?.gastricProblem}
                  />
                  <ConditionItem label="High BP" data={patient?.highBP} />
                  <ConditionItem label="Diabetes" data={patient?.diabetes} />
                  <ConditionItem
                    label="Monthly Injection"
                    data={patient?.monthlyInjection}
                  />
                  <ConditionItem
                    label="Pregnancy/Breast Feeding"
                    data={patient?.pregnancyBreastFeeding}
                  />
                  <ConditionItem
                    label="Cough/Cold/Fever"
                    data={patient?.coughColdFever}
                  />
                  <ConditionItem
                    label="Chest Pain/Heart"
                    data={patient?.chestpainHeartProblem}
                  />
                  <ConditionItem label="Epilepsy" data={patient?.epilepsy} />
                  <ConditionItem
                    label="Previous Extraction"
                    data={patient?.previousExtraction}
                  />
                  <ConditionItem
                    label="Previous Surgeries"
                    data={patient?.previoussurgeries}
                  />
                  <ConditionItem
                    label="COVID-19 (3 Doses)"
                    data={patient?.covid193Doses}
                  />
                  <ConditionItem
                    label="Drug History"
                    data={patient?.drugHistory}
                  />
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
                  Examination Notes
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <DetailItem
                    label="Extra Oral Examination"
                    value={patient?.extraOralExamination}
                  />
                  <DetailItem
                    label="Intra Oral Examination"
                    value={patient?.intraOralExamination}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PatientModal;
